import {
  // Note type icons
  Feedback as CustomerFeedbackIcon,
  Warning as AlertIcon,
  Assignment as GeneralIcon,
  ReportProblem as IncidentAccidentIcon,
  // Maintenance icon
  Engineering as MaintenanceIcon,
  // Fallback icons
  Info as InfoIcon,
  Flight as FlightIcon,
} from '@mui/icons-material';
import { NoteType, NotePriority } from '../types/api';

/**
 * Centralized utility for getting consistent icons and colors for notes and maintenance records
 */

// Icon size options
export type IconSize = 'small' | 'medium' | 'large';
export type IconSizeNumber = 12 | 16 | 20 | 24;

// Convert size names to numbers
const getSizeNumber = (size: IconSize): IconSizeNumber => {
  switch (size) {
    case 'small':
      return 16;
    case 'medium':
      return 20;
    case 'large':
      return 24;
    default:
      return 16;
  }
};

/**
 * Get the appropriate icon for a note type
 */
export const getNoteTypeIcon = (
  noteType: NoteType,
  priority: NotePriority,
  size: IconSize | IconSizeNumber = 'small'
) => {
  const iconSize = typeof size === 'number' ? size : getSizeNumber(size);
  const color = getNoteColor(priority);
  const iconProps = {
    fontSize: (typeof iconSize === 'number' ? undefined : iconSize) as
      | 'inherit'
      | 'small'
      | 'medium'
      | 'large'
      | undefined,
    sx: { color, fontSize: typeof iconSize === 'number' ? iconSize : undefined },
  };

  switch (noteType) {
    case 'customer_feedback':
      return <CustomerFeedbackIcon sx={iconProps} />;
    case 'alert':
      return <AlertIcon sx={iconProps} />;
    case 'general':
      return <GeneralIcon sx={iconProps} />;
    case 'incident_accident':
      return <IncidentAccidentIcon sx={iconProps} />;
    default:
      return <InfoIcon sx={iconProps} />;
  }
};

/**
 * Get the maintenance icon (always wrench)
 */
export const getMaintenanceIcon = (size: IconSize | IconSizeNumber = 'small') => {
  const iconSize = typeof size === 'number' ? size : getSizeNumber(size);
  const color = getMaintenanceColor();
  const iconProps = {
    fontSize: (typeof iconSize === 'number' ? undefined : iconSize) as
      | 'inherit'
      | 'small'
      | 'medium'
      | 'large'
      | undefined,
    sx: { color, fontSize: typeof iconSize === 'number' ? iconSize : undefined },
  };

  return <MaintenanceIcon sx={iconProps} />;
};

/**
 * Get color for note based on priority
 */
export const getNoteColor = (priority: NotePriority): string => {
  switch (priority) {
    case 'critical':
      return '#f44336'; // Red
    case 'high':
      return '#ff9800'; // Orange
    case 'medium':
      return '#2196f3'; // Blue
    case 'information':
      return '#9e9e9e'; // Gray
    case 'low':
      return '#4caf50'; // Green
    default:
      return '#4caf50'; // Default green
  }
};

/**
 * Get color for maintenance records (always orange)
 */
export const getMaintenanceColor = (): string => {
  return '#ff9800'; // Orange
};

/**
 * Get the most important note type from a list of notes
 * Used when multiple notes exist and we need to show one icon
 */
export const getMostImportantNoteType = (
  notes: Array<{ note_type: NoteType; priority: NotePriority }>
): { noteType: NoteType; priority: NotePriority } | null => {
  if (notes.length === 0) return null;

  // Priority order for note types: incident_accident > alert > customer_feedback > general
  const typeOrder: NoteType[] = ['incident_accident', 'alert', 'customer_feedback', 'general'];

  // First, find the most important type
  for (const type of typeOrder) {
    const notesOfType = notes.filter((n) => n.note_type === type);
    if (notesOfType.length > 0) {
      // Among notes of this type, get the highest priority
      const highestPriorityNote = getHighestPriorityNote(notesOfType);
      return {
        noteType: type,
        priority: highestPriorityNote.priority,
      };
    }
  }

  // Fallback to first note
  return {
    noteType: notes[0].note_type,
    priority: notes[0].priority,
  };
};

/**
 * Get the note with highest priority from a list
 */
export const getHighestPriorityNote = (
  notes: Array<{ priority: NotePriority }>
): { priority: NotePriority } => {
  const priorityOrder: NotePriority[] = ['critical', 'high', 'medium', 'low', 'information'];

  for (const priority of priorityOrder) {
    const noteWithPriority = notes.find((n) => n.priority === priority);
    if (noteWithPriority) {
      return noteWithPriority;
    }
  }

  return notes[0] || { priority: 'information' };
};

/**
 * Get icon for a mixed group (notes + maintenance)
 * Maintenance takes priority over notes
 */
export const getActivityIcon = (
  hasMaintenanceRecords: boolean,
  notes: Array<{ note_type: NoteType; priority: NotePriority }> = [],
  size: IconSize | IconSizeNumber = 'small'
) => {
  // Maintenance takes priority
  if (hasMaintenanceRecords) {
    return getMaintenanceIcon(size);
  }

  // Show note type icon if notes exist
  if (notes.length > 0) {
    const mostImportant = getMostImportantNoteType(notes);
    if (mostImportant) {
      return getNoteTypeIcon(mostImportant.noteType, mostImportant.priority, size);
    }
  }

  // Fallback to flight icon (for timeline events with no notes/maintenance)
  const iconSize = typeof size === 'number' ? size : getSizeNumber(size);
  return <FlightIcon sx={{ fontSize: iconSize, color: '#2196f3' }} />;
};
